import { get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import RoomItem, {
  type RoomSelectOption,
} from 'lumen/components/settings/recommendations/room-hierarchy/room-item';
import type { RoomOffsetHierarchy } from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './column-room-hierarchy.module.css';

interface SettingsRecommendationsRoomHierarchyColumnRoomHierarchySignature {
  Args: {
    activeRoomCode: string | null;
    editingRoomCode: string | null;
    isEditMode: boolean;
    level: number;
    levelHasRooms: boolean;
    onActivate: (roomCode: string | null) => void;
    onAddChildRoom: (parentRoomCode: string) => void;
    onCommitRoom: (
      oldRoomCode: string,
      newRoomCode: string,
      offsetValue: number | null,
    ) => void;
    onRemoveRoom: (roomCode: string) => void;
    onRoomEdit: (roomCode: string | null) => void;
    parentRoomCode?: string;
    pathRoomCode?: string;
    roomsInLevel: { roomCode: string; roomHierarchy: RoomOffsetHierarchy }[];
    usedRoomCodes: string[];
  };
}

export default class SettingsRecommendationsRoomHierarchyColumnRoomHierarchy extends Component<SettingsRecommendationsRoomHierarchyColumnRoomHierarchySignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  get availableRoomCodes(): string[] {
    // COA-3526 TODO: get correct static rate by caxxxPmsSettings.pms_root_rate_id
    if (!this.hotelAndMarketService.hotel.primaStaticRates[0]) {
      return [];
    }

    return this.hotelAndMarketService.hotel.primaStaticRates[0]
      .included_pms_rooms;
  }

  get roomSelectOptionsByRoomCode(): Record<string, RoomSelectOption[]> {
    const allUsedCodes = new Set(this.args.usedRoomCodes);
    const sorted = this.sortedRoomSelectOptions;

    return Object.fromEntries(
      ['', ...this.availableRoomCodes].map((roomCode) => [
        roomCode,
        sorted.filter(
          ({ label }) => label === roomCode || !allUsedCodes.has(label),
        ),
      ]),
    );
  }

  get sortedRoomSelectOptions(): RoomSelectOption[] {
    const baseRoomCode =
      this.hotelAndMarketService.hotel.pricingIntegration
        .pms_leading_room_pms_id;

    return [...this.availableRoomCodes]
      .sort((a, b) => a.localeCompare(b))
      .map((roomCode) => ({
        label: roomCode,
        ...(roomCode === baseRoomCode
          ? {
              badge: this.intl.t(
                'pricing_assistant.settings.panel.section.rooms.leading_room',
              ),
              badgeType: 'neutral',
            }
          : {}),
      }));
  }

  <template>
    <div
      class={{styles.level-column}}
      data-has-rooms={{if @levelHasRooms "true"}}
    >
      <PrismCard @contentLayout="noPadding">
        <:header as |Header|>
          <Header.title>
            <p
              class="text-200--medium {{styles.level-header}}"
              data-level-header
            >{{t
                "pricing_assistant.recommendations.settings.room_hierarchy.level_label"
                level=@level
              }}</p>
          </Header.title>
        </:header>
        <:content>
          <div class={{styles.room-list}}>
            {{#each @roomsInLevel as |room|}}
              <RoomItem
                @activeRoomCode={{@activeRoomCode}}
                @editingRoomCode={{@editingRoomCode}}
                @isEditMode={{@isEditMode}}
                @onActivate={{@onActivate}}
                @onAddChildRoom={{@onAddChildRoom}}
                @onCommitRoom={{@onCommitRoom}}
                @onRemoveRoom={{@onRemoveRoom}}
                @onRoomEdit={{@onRoomEdit}}
                @parentRoomCode={{@parentRoomCode}}
                @pathRoomCode={{@pathRoomCode}}
                @room={{room}}
                @roomSelectOptions={{get
                  this.roomSelectOptionsByRoomCode
                  room.roomCode
                }}
              />
            {{/each}}
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
